import { uniqBy } from '@benzinga/utils';
import { Notification, ReadStatus, NotificationId, MarkReadUnreadParams } from './entities';

export class NotificationsStore {
  private notifications: Notification[] = [];
  private totalCount?: number;
  private unreadCount?: number;
  private lastId: {
    [key in ReadStatus]?: number;
  } = {};
  private fullyLoaded: {
    [key in ReadStatus]?: boolean;
  } = {};

  public clear = () => {
    this.notifications = [];
    this.totalCount = undefined;
    this.unreadCount = undefined;
    this.lastId = {};
    this.fullyLoaded = {};
  };

  public getNotification = (id: NotificationId): Notification | undefined =>
    this.notifications.find(notification => notification.id === id);

  public addNewNotification = (notification: Notification): void => {
    notification.created_at = new Date().toISOString();
    this.notifications = [notification, ...this.notifications];
    this.totalCount = this.totalCount ? this.totalCount + 1 : 1;
    this.unreadCount = this.unreadCount ? this.unreadCount + 1 : 1;
  };

  public addNotifications = (notifications: Notification[]): void => {
    this.notifications = uniqBy(this.notifications.concat(notifications), 'id').sort((a, b) => b.id - a.id);
  };

  public getNotifications = (): Notification[] => this.notifications;

  public setTotalCount = (totalCount: number): void => {
    this.totalCount = totalCount;
  };

  public getTotalCount = (): number | undefined => this.totalCount;

  public setUnreadCount = (unreadCount: number): void => {
    this.unreadCount = unreadCount;
  };

  public getUnreadCount = (): number | undefined => this.unreadCount;

  public setLastId = (id: number, status: ReadStatus): void => {
    this.lastId[status] = id;
  };

  public getLastId = (status: ReadStatus): number | undefined => {
    const allId = this.lastId.all;
    const statusId = this.lastId[status];
    if (allId && statusId && allId >= statusId) {
      return allId;
    }
    return statusId ?? allId;
  };

  public setFullyLoaded = (status: ReadStatus): void => {
    this.fullyLoaded[status] = true;
  };

  public hasMore = (status: ReadStatus): boolean | undefined => !this.fullyLoaded[status];

  public markReadUnread = ({ readAll, readIds, unreadIds }: MarkReadUnreadParams) => {
    if (readAll) {
      this.unreadCount = 0;
    }
    this.notifications = this.notifications.map(notification => {
      let { read } = notification;
      if (readAll) {
        read = true;
      } else if (readIds.includes(notification.id)) {
        read = true;
        this.unreadCount = this.unreadCount ? this.unreadCount - 1 : 0;
      } else if (unreadIds.includes(notification.id)) {
        read = false;
        this.unreadCount = this.unreadCount ? this.unreadCount + 1 : 1;
      }
      return {
        ...notification,
        read,
      };
    });
  };
}
